// src/api/activityApi.ts

import { baseApi } from './baseApi';

// Types
export type ActivityAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'completed'
  | 'assigned'
  | 'commented'
  | 'scheduled'
  | 'called'
  | 'missed_call'
  | 'voicemail'
  | 'message_sent'
  | 'message_received'
  | 'email_sent'
  | 'email_received'
  | 'sms_sent'
  | 'sms_received'
  | 'stage_changed'
  | 'status_changed'
  | 'won'
  | 'lost'
  | 'ai_action'
  | 'form_submitted'
  | 'file_uploaded'
  | 'file_shared';

export type ActivityEntityType =
  | 'contact'
  | 'task'
  | 'opportunity'
  | 'event'
  | 'thread'
  | 'call'
  | 'voicemail'
  | 'channel'
  | 'project'
  | 'form'
  | 'message'
  | 'conversation'
  | 'system'
  | 'circle';

export interface ActivityActor {
  id: string;
  fullName: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface ActivityEvent {
  id: string;
  workspaceId: string;
  actorUserId: string | null;
  entityType: ActivityEntityType;
  entityId: string;
  action: ActivityAction;
  summary: string;
  data: Record<string, unknown> | null;
  createdAt: string;
  actorUser?: ActivityActor | null;
}

export interface ActivityResponse {
  activities: ActivityEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface EntityActivityResponse {
  activities: ActivityEvent[];
}

export interface ActivityStatsResponse {
  byAction: Array<{ action: string; count: number }>;
}

export interface GetActivityParams {
  entityType?: ActivityEntityType;
  entityId?: string;
  actorUserId?: string;
  action?: ActivityAction;
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
}

export const activityApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getActivity: builder.query<ActivityResponse, GetActivityParams | void>({
      query: (params) => ({
        url: '/activity',
        params: params ? {
          ...(params.entityType && { entityType: params.entityType }),
          ...(params.entityId && { entityId: params.entityId }),
          ...(params.actorUserId && { actorUserId: params.actorUserId }),
          ...(params.action && { action: params.action }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
        } : {},
      }),
      providesTags: [{ type: 'Activity', id: 'LIST' }],
    }),

    getEntityActivity: builder.query<EntityActivityResponse, { entityType: string; entityId: string; limit?: number }>({
      query: ({ entityType, entityId, limit }) => ({
        url: `/activity/entity/${entityType}/${entityId}`,
        params: limit ? { limit } : {},
      }),
      providesTags: (result, error, { entityType, entityId }) => [
        { type: 'Activity', id: `${entityType}-${entityId}` },
      ],
    }),

    getUserActivity: builder.query<EntityActivityResponse, { userId: string; limit?: number }>({
      query: ({ userId, limit }) => ({
        url: `/activity/user/${userId}`,
        params: limit ? { limit } : {},
      }),
      providesTags: (result, error, { userId }) => [
        { type: 'Activity', id: `user-${userId}` },
      ],
    }),

    getActivityStats: builder.query<ActivityStatsResponse, { startDate?: string; endDate?: string } | void>({
      query: (params) => ({
        url: '/activity/stats',
        params: params ? {
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
        } : {},
      }),
      providesTags: [{ type: 'Activity', id: 'STATS' }],
    }),
  }),
});

export const {
  useGetActivityQuery,
  useGetEntityActivityQuery,
  useGetUserActivityQuery,
  useGetActivityStatsQuery,
} = activityApi;
